// src/views/home.js
import React, { useRef, useState, Fragment, useEffect } from 'react'
import { Helmet } from 'react-helmet'

import Navbar8 from '../components/navbar8'
import Hero17 from '../components/hero17'
import Features24 from '../components/features24'
import CTA26 from '../components/cta26'
import Features25 from '../components/features25'
import Pricing14 from '../components/pricing14'
import Steps2 from '../components/steps2'
import Testimonial17 from '../components/testimonial17'
import Contact10 from '../components/contact10'
import Footer4 from '../components/footer4'
import OnboardingChatbot from '../components/OnboardingChatbot'
import '../components/OnboardingChatbot.css'
import ClientSetupForm from '../components/ClientSetupForm'
import '../components/ClientSetupForm.css'
import PacoAvatar from '../components/PacoAvatar'
import './home.css'
import { useTranslation } from 'react-i18next'

// Authentication session & logo import matching Navbar8
import useAuthSession from '../hooks/useAuthSession'
import { clearAuthSession } from '../utils/auth'
import MercaditoLogo from '../assets/MercaditoAI_Logo.jpg'

const Home = (props) => {
  const [showChatBot, setShowChatBot] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [botSummaryData, setBotSummaryData] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const pricingRef = useRef(null);
  const auth = useAuthSession();
  const { token } = auth;

  // Sync authentication state exactly as in Navbar8
  useEffect(() => {
    const checkToken = () => {
      if (!token) {
        setIsLoggedIn(false);
        return;
      }
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          clearAuthSession();
          setIsLoggedIn(false);
        } else {
          setIsLoggedIn(true);
        }
      } catch {
        setIsLoggedIn(true);
      }
    };

    checkToken();
    window.addEventListener('storage', checkToken);
    return () => window.removeEventListener('storage', checkToken);
  }, [token]);

  const scrollToPricing = () => {
    setMobileMenuOpen(false);
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLanguageChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };

  const handleNavigationSelect = (e) => {
    const route = e.target.value;
    if (route) {
      setMobileMenuOpen(false);
      window.location.href = route;
    }
  };

  useEffect(() => {
    if (showChatBot || mobileMenuOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showChatBot, mobileMenuOpen]);

  return (
    <div className="home-container">
      <Helmet>
        <title>Mercadito AI</title>
        <meta property="og:title" content="Mercadito AI" />
      </Helmet>

      {/* =========================================================
          MOBILE NAVBAR HEADER (<= 768px Viewports)
         ========================================================= */}
      <header className="home-mobile-nav">
        <div className="home-mobile-nav-inner">
          <a href="/" className="home-mobile-logo">
            <img
              src={MercaditoLogo}
              alt="Mercadito AI"
              className="home-mobile-logo-img"
            />
            <span className="home-mobile-logo-text">Mercadito AI</span>
          </a>

          <button 
            type="button"
            className="home-hamburger-btn"
            onClick={() => {
              setMobileMenuOpen(prev => !prev);
              if (mobileMenuOpen) setHowItWorksOpen(false);
            }}
            aria-label="Toggle Navigation"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Slide-out mobile drawer */}
        <div className={`home-mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
          <nav className="home-mobile-drawer-links">
            <a href="/" onClick={() => setMobileMenuOpen(false)}>
              {t('home.navBar.link1', 'Home')}
            </a>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>
              {t('home.navBar.link2', 'Common Barriers')}
            </a>
            <a href="#testimonialRef" onClick={() => setMobileMenuOpen(false)}>
              {t('home.navBar.link3', 'Success Stories')}
            </a>

            {/* How It Works Accordion Trigger */}
            <button 
              type="button" 
              className="home-mobile-link-btn home-mobile-hiw-trigger"
              onClick={() => setHowItWorksOpen(prev => !prev)}
            >
              <span>{t('home.navBar.link4', t('home.navBar.howItWorks', 'How It Works'))}</span>
              <span className="home-mobile-hiw-chevron">{howItWorksOpen ? '▴' : '▾'}</span>
            </button>

            {/* How It Works Expanded Content */}
            {howItWorksOpen && (
              <div className="home-mobile-hiw-content">
                <h3 className="home-mobile-hiw-heading">
                  {t('home.navBar.heading', 'Boost Your Business in Just 4 Steps')}
                </h3>
                
                <div className="home-mobile-hiw-avatar-section">
                  <PacoAvatar style={{ width: '120px', height: 'auto' }} />
                  <p className="home-mobile-hiw-subtitle">
                    {t('home.navBar.content', 'Paco, our chatbot, is with you on each of the steps.')}
                  </p>
                </div>

                <div className="home-mobile-hiw-steps">
                  {/* Step 1 */}
                  <div className="home-mobile-hiw-step">
                    <img
                      src="/images/steps/step1-pick-plan.jpg"
                      alt="Step 1"
                      className="home-mobile-hiw-step-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="home-mobile-hiw-step-details">
                      <strong className="home-mobile-hiw-step-title">
                        {t('home.navBar.title1', '1. Paco learns about your business')}
                      </strong>
                      <span className="home-mobile-hiw-step-desc">
                        {t('home.navBar.description1', 'Paco asks quick questions and suggests the best tools - all tailored to your business.')}
                      </span>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="home-mobile-hiw-step">
                    <img
                      src="/images/steps/step2-signup.jpg"
                      alt="Step 2"
                      className="home-mobile-hiw-step-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="home-mobile-hiw-step-details">
                      <strong className="home-mobile-hiw-step-title">
                        {t('home.navBar.title2', '2. Sign Up Easily')}
                      </strong>
                      <span className="home-mobile-hiw-step-desc">
                        {t('home.navBar.description2', 'Paco fills in your info: name, location, phone, and email. Done in minutes — no hassle')}
                      </span>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="home-mobile-hiw-step">
                    <img
                      src="/images/steps/step3-customize.jpg"
                      alt="Step 3"
                      className="home-mobile-hiw-step-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="home-mobile-hiw-step-details">
                      <strong className="home-mobile-hiw-step-title">
                        {t('home.navBar.title3', '3. Activate your business')}
                      </strong>
                      <span className="home-mobile-hiw-step-desc">
                        {t('home.navBar.description3', 'Paco sets up everything—your catalog, online payments, QR flyers, and more. — you’re ready to sell!')}
                      </span>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="home-mobile-hiw-step">
                    <img
                      src="/images/steps/step4-grow.jpg"
                      alt="Step 4"
                      className="home-mobile-hiw-step-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="home-mobile-hiw-step-details">
                      <strong className="home-mobile-hiw-step-title">
                        {t('home.navBar.title4', '4. Grow Smarter')}
                      </strong>
                      <span className="home-mobile-hiw-step-desc">
                        {t('home.navBar.description4', 'You deliver great products. Mercadito AI handles the tech, automation, and growth tools — like your own digital assistant.')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DYNAMIC: Shows "Login" when logged out / expired; Shows "Navigate to page" dropdown when logged in */}
            {!isLoggedIn ? (
              <a 
                href="/login" 
                className="home-mobile-login-btn"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{t('dropDown.login', 'Login')}</span>
                <span className="home-mobile-login-arrow">→</span>
              </a>
            ) : (
              <div className="home-mobile-select-container">
                <select
                  className="home-mobile-nav-dropdown"
                  defaultValue=""
                  onChange={handleNavigationSelect}
                >
                  <option value="" disabled>
                    {t('home.navBar.navigateToPage', 'Navigate to page ▾')}
                  </option>
                  <option value="/dashboard">{t('dropDown.dashboard-page', 'Dashboard')}</option>
                  <option value="/crm-lite">{t('dropDown.crm-lite-page', 'CRM Lite')}</option>
                  <option value="/clients">{t('dropDown.clients-page', 'Clients')}</option>
                  <option value="/scheduler">{t('dropDown.scheduler-page', 'Scheduler')}</option>
                  <option value="/commerce">{t('dropDown.commerce-page', 'Commerce')}</option>
                  <option value="/dispatcher">{t('dropDown.dispatcher-page', 'Dispatcher')}</option>
                  <option value="/analytics">{t('dropDown.analytics-page', 'Analytics')}</option>
                  <option value="/qr-code">{t('dropDown.qr-page', 'QR Codes')}</option>
                  <option value="/support">{t('dropDown.support-page', 'Support Center')}</option>
                  <option value="/tutorial">{t('dropDown.tutorial-page', 'Tutorial')}</option>
                </select>
              </div>
            )}
          </nav>

          <div className="home-mobile-drawer-footer">
            {/* Language Selector */}
            <div className="home-mobile-lang-container">
              <select 
                id="mobile-lang-select"
                className="home-mobile-lang-select" 
                value={i18n.language} 
                onChange={handleLanguageChange}
              >
                <option value="en">English ▾</option>
                <option value="es">Español ▾</option>
              </select>
            </div>

            {/* Action 1: Start Trial */}
            <button 
              className="btn-mobile-trial"
              onClick={() => {
                setMobileMenuOpen(false);
                setShowChatBot(true);
              }}
            >
              {t('home.hero.action1', 'Start Trial')}
            </button>

            {/* Action 2: View Demo */}
            <button 
              className="btn-mobile-demo"
              onClick={() => {
                setMobileMenuOpen(false);
                setShowChatBot(true);
              }}
            >
              {t('home.navBar.action2', 'View Demo')}
            </button>
          </div>
        </div>
      </header>

      {/* =========================================================
          DESKTOP NAVBAR (> 768px Viewports)
         ========================================================= */}
      <div className="home-desktop-nav-wrapper">
        <Navbar8
          setShowChatBot={setShowChatBot}
          heading={
            <Fragment>
              {t('home.navBar.heading')}
            </Fragment>
          }
          content={
            <Fragment>
              {t('home.navBar.content')}
            </Fragment>
          }
          page1Description={
            <Fragment>
              <span className="home-text108">{t('home.navBar.description1')}</span>
            </Fragment>
          }
          page2Description={
            <Fragment>
              <span className="home-text109">{t('home.navBar.description2')}</span>
            </Fragment>
          }
          page3Description={
            <Fragment>
              <span className="home-text112">{t('home.navBar.description3')}</span>
            </Fragment>
          }
          page1={
            <Fragment>
              <span className="home-text103">{t('home.navBar.title1')}</span>
            </Fragment>
          }
          page2={
            <Fragment>
              <span className="home-text106">{t('home.navBar.title2')}</span>
            </Fragment>
          }
          page3={
            <Fragment>
              <span className="home-text111">{t('home.navBar.title3')}</span>
            </Fragment>
          }
          link1={
            <Fragment>
              <span className="home-text104">{t('home.navBar.link1')}</span>
            </Fragment>
          }
          link2={
            <Fragment>
              <span className="home-text102">{t('home.navBar.link2')}</span>
            </Fragment>
          }
          link3={
            <Fragment>
              <span className="home-text110">
                <a href="#testimonialRef">{t('home.navBar.link3')}</a>
              </span>
            </Fragment>
          }
          action0={
            <Fragment>
              <select className="navbar8-language-select" onChange={handleLanguageChange} value={i18n.language}>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </Fragment>
          }
          action1={
            <Fragment>
              <span className="home-text101">{t('home.navBar.action1')}</span>
            </Fragment>
          }
          action2={
            <Fragment>
              <span className="home-text113">{t('home.navBar.action2')}</span>
            </Fragment>
          }
        />
      </div>

      <Hero17
        heading1={
          <Fragment>
            <span className="home-text116">
              {t('home.hero.heading')}
            </span>
          </Fragment>
        }
        content1={
          <Fragment>
            <span className="home-text117">
              {t('home.hero.content')}
            </span>
          </Fragment>
        }
        action1={
          <Fragment>
            <span className="home-text115">{t('home.hero.action1')}</span>
          </Fragment>
        }
        action2={
          <Fragment>
            <span onClick={scrollToPricing} className="home-text114">{t('home.hero.action2')}</span>
          </Fragment>
        }
      />

      <CTA26
        heading1={
          <Fragment>
            <span className="home-text124">
              {t('home.CTA26.heading')}
            </span>
          </Fragment>
        }
        content1={
          <Fragment>
            <span className="home-text125">
              {t('home.CTA26.content')}
            </span>
          </Fragment>
        }
        action1={
          <Fragment>
            <span className="home-text126">{t('home.CTA26.action')}</span>
          </Fragment>
        }
        onRegisterClick={() => setShowChatBot(true)}
      />

      {showChatBot && (
        <div className="home-chatbot-container">
          <button className="chatbot-close-button" onClick={() => setShowChatBot(false)}>
            ×
          </button>
          <OnboardingChatbot
            onClientFormRequested={(summary) => {
              setBotSummaryData(summary);
              setShowForm(true);
            }}
          />
          {showForm && (
            <ClientSetupForm
              initialData={botSummaryData}
              onComplete={(status) => {
                if (status === 'success') {
                  setShowForm(false);
                }
              }}
            />
          )}
        </div>
      )}

      <Features24
        feature1Title={
          <Fragment>
            <span className="home-text121">{t('features.whatsappChatbot.title')}</span>
          </Fragment>
        }
        feature1Description={
          <Fragment>
            <span className="home-text122">{t('features.whatsappChatbot.description')}</span>
          </Fragment>
        }
        feature2Title={
          <Fragment>
            <span className="home-text123">{t('features.digitalCatalog.title')}</span>
          </Fragment>
        }
        feature2Description={
          <Fragment>
            <span className="home-text120">{t('features.digitalCatalog.description')}</span>
          </Fragment>
        }
        feature3Title={
          <Fragment>
            <span className="home-text119">{t('features.onlineOrders.title')}</span>
          </Fragment>
        }
        feature3Description={
          <Fragment>
            <span className="home-text118">{t('features.onlineOrders.description')}</span>
          </Fragment>
        }
      />

      <Features25
        feature1Title={
          <Fragment>
            <span className="home-text130">{t('features.onlinePayments.title')}</span>
          </Fragment>
        }
        feature1Description={
          <Fragment>
            <span className="home-text128">{t('features.onlinePayments.description')}</span>
          </Fragment>
        }
        feature2Title={
          <Fragment>
            <span className="home-text129">{t('features.crmLite.title')}</span>
          </Fragment>
        }
        feature2Description={
          <Fragment>
            <span className="home-text131">{t('features.crmLite.description')}</span>
          </Fragment>
        }
        feature3Title={
          <Fragment>
            <span className="home-text132">{t('features.ads.title')}</span>
          </Fragment>
        }
        feature3Description={
          <Fragment>
            <span className="home-text127">{t('features.ads.description')}</span>
          </Fragment>
        }
        feature4Title={
          <Fragment>
            <span className="home-text221">{t('features.qrFlyers.title')}</span>
          </Fragment>
        }
        feature4Description={
          <Fragment>
            <span className="home-text220">{t('features.qrFlyers.description')}</span>
          </Fragment>
        }
      />

      <div ref={pricingRef}>
        <Pricing14
          heading1={
            <Fragment>
              <span className="home-text158">{t('home.pricing.heading')}</span>
            </Fragment>
          }
          content1={
            <Fragment>
              <span className="home-text156">
                {t('home.pricing.content1')}
              </span>
            </Fragment>
          }
          content2={
            <Fragment>
              <span className="home-text180">
                {t('home.pricing.content2')}
                <span
                  dangerouslySetInnerHTML={{
                    __html: ' ',
                  }}
                />
              </span>
            </Fragment>
          }
          plan1={
            <Fragment>
              <span className="home-text160">{t('home.pricing.plan1.title')}</span>
            </Fragment>
          }
          plan11={
            <Fragment>
              <span className="home-text135">{t('home.pricing.plan1.title')}</span>
            </Fragment>
          }
          plan1Feature1={
            <Fragment>
              <span className="home-text178">{t('home.pricing.plan1.feature1')}</span>
            </Fragment>
          }
          plan1Feature11={
            <Fragment>
              <span className="home-text162">{t('home.pricing.plan1.feature1')}</span>
            </Fragment>
          }
          plan1Feature2={
            <Fragment>
              <span className="home-text139">{t('home.pricing.plan1.feature2')}</span>
            </Fragment>
          }
          plan1Feature21={
            <Fragment>
              <span className="home-text163">{t('home.pricing.plan1.feature2')}</span>
            </Fragment>
          }
          plan1Feature3={
            <Fragment>
              <span className="home-text176">{t('home.pricing.plan1.feature3')}</span>
            </Fragment>
          }
          plan1Feature31={
            <Fragment>
              <span className="home-text175">{t('home.pricing.plan1.feature3')}</span>
            </Fragment>
          }
          plan1Price={
            <Fragment>
              <span className="home-text182">{t('home.pricing.plan1.price1')}</span>
            </Fragment>
          }
          plan1Price1={
            <Fragment>
              <span className="home-text151">{t('home.pricing.plan1.price11')}</span>
            </Fragment>
          }
          plan1Action={
            <Fragment>
              <span className="home-text136">{t('home.pricing.plan1.action')}</span>
            </Fragment>
          }
          plan1Action1={
            <Fragment>
              <span className="home-text147">{t('home.pricing.plan1.action')}</span>
            </Fragment>
          }
          plan1Yearly={
            <Fragment>
              <span className="home-text177">{t('home.pricing.plan1.yearly1')}</span>
            </Fragment>
          }
          plan1Yearly1={
            <Fragment>
              <span className="home-text170">{t('home.pricing.plan1.yearly11')}</span>
            </Fragment>
          }
          plan2={
            <Fragment>
              <span className="home-text152">{t('home.pricing.plan2.title')}</span>
            </Fragment>
          }
          plan21={
            <Fragment>
              <span className="home-text161">{t('home.pricing.plan2.title')}</span>
            </Fragment>
          }
          plan2Feature1={
            <Fragment>
              <span className="home-text157">{t('home.pricing.plan2.feature1')}</span>
            </Fragment>
          }
          plan2Feature11={
            <Fragment>
              <span className="home-text140">{t('home.pricing.plan2.feature1')}</span>
            </Fragment>
          }
          plan2Feature2={
            <Fragment>
              <span className="home-text143">{t('home.pricing.plan2.feature2')}</span>
            </Fragment>
          }
          plan2Feature21={
            <Fragment>
              <span className="home-text153">{t('home.pricing.plan2.feature2')}</span>
            </Fragment>
          }
          plan2Feature3={
            <Fragment>
              <span className="home-text150">{t('home.pricing.plan2.feature3')}</span>
            </Fragment>
          }
          plan2Feature31={
            <Fragment>
              <span className="home-text168">{t('home.pricing.plan2.feature3')}</span>
            </Fragment>
          }
          plan2Feature4={
            <Fragment>
              <span className="home-text145">{t('home.pricing.plan2.feature4')}</span>
            </Fragment>
          }
          plan2Feature41={
            <Fragment>
              <span className="home-text142">{t('home.pricing.plan2.feature4')}</span>
            </Fragment>
          }
          plan2Price={
            <Fragment>
              <span className="home-text166">{t('home.pricing.plan2.price2')}</span>
            </Fragment>
          }
          plan2Price1={
            <Fragment>
              <span className="home-text171">{t('home.pricing.plan2.price22')}</span>
            </Fragment>
          }
          plan2Action={
            <Fragment>
              <span className="home-text148">{t('home.pricing.plan2.action')}</span>
            </Fragment>
          }
          plan2Action1={
            <Fragment>
              <span className="home-text154">{t('home.pricing.plan2.action')}</span>
            </Fragment>
          }
          plan2Yearly={
            <Fragment>
              <span className="home-text146">{t('home.pricing.plan2.yearly2')}</span>
            </Fragment>
          }
          plan2Yearly1={
            <Fragment>
              <span className="home-text165">{t('home.pricing.plan2.yearly22')}</span>
            </Fragment>
          }
          plan3={
            <Fragment>
              <span className="home-text183">{t('home.pricing.plan3.title')}</span>
            </Fragment>
          }
          plan31={
            <Fragment>
              <span className="home-text137">{t('home.pricing.plan3.title')}</span>
            </Fragment>
          }
          plan3Feature1={
            <Fragment>
              <span className="home-text149">{t('home.pricing.plan3.feature1')}</span>
            </Fragment>
          }
          plan3Feature11={
            <Fragment>
              <span className="home-text169">{t('home.pricing.plan3.feature1')}</span>
            </Fragment>
          }
          plan3Feature2={
            <Fragment>
              <span className="home-text155">{t('home.pricing.plan3.feature2')}</span>
            </Fragment>
          }
          plan3Feature21={
            <Fragment>
              <span className="home-text144">{t('home.pricing.plan3.feature2')}</span>
            </Fragment>
          }
          plan3Feature3={
            <Fragment>
              <span className="home-text179">{t('home.pricing.plan3.feature3')}</span>
            </Fragment>
          }
          plan3Feature31={
            <Fragment>
              <span className="home-text159">{t('home.pricing.plan3.feature3')}</span>
            </Fragment>
          }
          plan3Feature4={
            <Fragment>
              <span className="home-text173">{t('home.pricing.plan3.feature4')}</span>
            </Fragment>
          }
          plan3Feature41={
            <Fragment>
              <span className="home-text138">{t('home.pricing.plan3.feature4')}</span>
            </Fragment>
          }
          plan3Feature5={
            <Fragment>
              <span className="home-text164">{t('home.pricing.plan3.feature5')}</span>
            </Fragment>
          }
          plan3Feature51={
            <Fragment>
              <span className="home-text141">{t('home.pricing.plan3.feature5')}</span>
            </Fragment>
          }
          plan3Price={
            <Fragment>
              <span className="home-text133">{t('home.pricing.plan3.price3')}</span>
            </Fragment>
          }
          plan3Price1={
            <Fragment>
              <span className="home-text174">{t('home.pricing.plan3.price33')}</span>
            </Fragment>
          }
          plan3Action={
            <Fragment>
              <span className="home-text134">{t('home.pricing.plan3.action')}</span>
            </Fragment>
          }
          plan3Action1={
            <Fragment>
              <span className="home-text181">{t('home.pricing.plan3.action')}</span>
            </Fragment>
          }
          plan3Yearly={
            <Fragment>
              <span className="home-text172">{t('home.pricing.plan3.yearly3')}</span>
            </Fragment>
          }
          plan3Yearly1={
            <Fragment>
              <span className="home-text167">{t('home.pricing.plan3.yearly33')}</span>
            </Fragment>
          }
        />
      </div>

      <Steps2
        step1Title={
          <Fragment>
            <span className="home-text188">{t('home.step.step1.title')}</span>
          </Fragment>
        }
        step1Description={
          <Fragment>
            <span className="home-text184">
              {t('home.step.step1.description')}
            </span>
          </Fragment>
        }
        step2Title={
          <Fragment>
            <span className="home-text186">{t('home.step.step2.title')}</span>
          </Fragment>
        }
        step2Description={
          <Fragment>
            <span className="home-text187">
              {t('home.step.step2.description')}
            </span>
          </Fragment>
        }
        step3Title={
          <Fragment>
            <span className="home-text189">
              {t('home.step.step3.title')}
            </span>
          </Fragment>
        }
        step3Description={
          <Fragment>
            <span className="home-text185">
              {t('home.step.step3.description')}
            </span>
          </Fragment>
        }
        step4Title={
          <Fragment>
            <span className="home-text191">{t('home.step.step4.title')}</span>
          </Fragment>
        }
        step4Description={
          <Fragment>
            <span className="home-text190">
              {t('home.step.step4.description')}
            </span>
          </Fragment>
        }
      />

      <div id="testimonialRef">
        <Testimonial17
          heading1={
            <Fragment>
              <span className="home-text203">
                {t('home.testimonial.heading')}</span>
            </Fragment>
          }
          content1={
            <Fragment>
              <span className="home-text200">
                {t('home.testimonial.content')}
              </span>
            </Fragment>
          }
          author1Name={
            <Fragment>
              <span className="home-text194">{t('home.testimonial.client1.name')}</span>
            </Fragment>
          }
          author1Position={
            <Fragment>
              <span className="home-text193">{t('home.testimonial.client1.occupation')}</span>
            </Fragment>
          }
          review1={
            <Fragment>
              <span className="home-text202">
                {t('home.testimonial.client1.review')}
              </span>
            </Fragment>
          }
          author2Name={
            <Fragment>
              <span className="home-text197">{t('home.testimonial.client2.name')}</span>
            </Fragment>
          }
          author2Position={
            <Fragment>
              <span className="home-text192">{t('home.testimonial.client2.occupation')}</span>
            </Fragment>
          }
          review2={
            <Fragment>
              <span className="home-text196">
                {t('home.testimonial.client2.review')}
              </span>
            </Fragment>
          }
          author3Name={
            <Fragment>
              <span className="home-text195">{t('home.testimonial.client3.name')}</span>
            </Fragment>
          }
          author3Position={
            <Fragment>
              <span className="home-text201">{t('home.testimonial.client3.occupation')}</span>
            </Fragment>
          }
          review3={
            <Fragment>
              <span className="home-text204">
                {t('home.testimonial.client3.review')}
              </span>
            </Fragment>
          }
          author4Name={
            <Fragment>
              <span className="home-text199">{t('home.testimonial.client4.name')}</span>
            </Fragment>
          }
          author4Position={
            <Fragment>
              <span className="home-text198">{t('home.testimonial.client4.occupation')}</span>
            </Fragment>
          }
          review4={
            <Fragment>
              <span className="home-text205">
                {t('home.testimonial.client4.review')}
              </span>
            </Fragment>
          }
        />
      </div>

      <Contact10
        heading1={
          <Fragment>
            <span className="home-text208">{t('home.contact.heading')}</span>
          </Fragment>
        }
        content1={
          <Fragment>
            <span className="home-text206">
              {t('home.contact.content')}
            </span>
          </Fragment>
        }
        location1={
          <Fragment>
            <span className="home-text210">
              {t('home.contact.location')}
            </span>
          </Fragment>
        }
        location1Description={
          <Fragment>
            <span className="home-text207">
              {t('home.contact.locationInfo')}
            </span>
          </Fragment>
        }
        location2={
          <Fragment>
            <span className="home-text211">{t('home.contact.phone')}</span>
          </Fragment>
        }
        location2Description={
          <Fragment>
            <span className="home-text209">
              {t('home.contact.phoneInfo')}
            </span>
          </Fragment>
        }
      />

      <Footer4
        link1={
          <Fragment>
            <span className="home-text214">{t('home.footer.link1')}</span>
          </Fragment>
        }
        link2={
          <Fragment>
            <span className="home-text216">{t('home.footer.link2')}</span>
          </Fragment>
        }
        link3={
          <Fragment>
            <span className="home-text213">{t('home.footer.link3')}</span>
          </Fragment>
        }
        link4={
          <Fragment>
            <span className="home-text217">{t('home.footer.link4')}</span>
          </Fragment>
        }
        link5={
          <Fragment>
            <span className="home-text212">{t('home.footer.link5')}</span>
          </Fragment>
        }
        termsLink={
          <Fragment>
            <span className="home-text215">{t('home.footer.terms')}</span>
          </Fragment>
        }
        cookiesLink={
          <Fragment>
            <span className="home-text218">{t('home.footer.cookies')}</span>
          </Fragment>
        }
        privacyLink={
          <Fragment>
            <span className="home-text219">{t('home.footer.privacy')}</span>
          </Fragment>
        }
      />
    </div>
  )
}

export default Home